"use client";

import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence } from "motion/react";
import { memo, useCallback, useEffect, useId, useMemo } from "react";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";
import { StackNavigationProvider } from "./context";
import { StackScreenRenderer } from "./StackScreenRenderer";
import { useStackById, useStackStore } from "./store";

import type { ComponentType } from "react";
import type { ScreensConfig, StackItem, StackNavigatorProps } from "./types";

const BackIcon = memo(function BackIcon() {
  return <FontAwesomeIcon icon={faChevronLeft} className="size-4" />;
});

function buildComponentMap(
  screens: ScreensConfig
): Record<string, ComponentType<Record<string, unknown>>> {
  const map: Record<string, ComponentType<Record<string, unknown>>> = {};
  for (const name in screens) {
    map[name] = screens[name].component;
  }
  return map;
}

/**
 * Stack navigator component.
 * Manages screen transitions and scroll preservation.
 * Includes a built-in header with back button and title.
 *
 * Must be used within a `StackStoreProvider`.
 *
 * Screens can use `useStackNavigation()` hook to access push/pop/replace/reset.
 *
 * @example
 * // Basic usage with auto-generated stackId
 * <StackStoreProvider>
 *   <StackNavigator screens={screens} initialRouteName="home" />
 * </StackStoreProvider>
 *
 * @example
 * // Multiple stacks with explicit IDs
 * <StackStoreProvider>
 *   <StackNavigator stackId="vehicles" screens={vehicleScreens} initialRouteName="list" />
 *   <StackNavigator stackId="incidents" screens={incidentScreens} initialRouteName="list" />
 * </StackStoreProvider>
 *
 * @example
 * // Control stack from outside
 * const push = useStackStore(state => state.push);
 * push("vehicles", "detail", { id: 123 });
 */
export function StackNavigator<T extends ScreensConfig>({
  stackId: externalStackId,
  screens,
  initialRouteName,
  className,
  screenClassName,
  showHeader = true,
  headerEndContent,
}: StackNavigatorProps<T>) {
  // Generate a stable ID if not provided
  const generatedId = useId();
  const stackId = externalStackId ?? generatedId;

  // Get store actions
  const initStack = useStackStore(state => state.initStack);
  const pop = useStackStore(state => state.pop);

  // Get this stack's state
  const { stack, canPop, currentRoute } = useStackById(stackId);

  // Build component map
  const componentMap = useMemo(() => buildComponentMap(screens), [screens]);

  // Initialize stack on mount if empty
  const defaultRoute = initialRouteName ?? (Object.keys(screens)[0] as keyof T & string);
  useEffect(() => {
    if (defaultRoute) {
      initStack(stackId, defaultRoute);
    }
  }, [stackId, defaultRoute, initStack]);

  const handlePop = useCallback(() => {
    pop(stackId);
  }, [pop, stackId]);

  const currentTitle = screens[currentRoute]?.title;
  const containerClassName = cn("relative flex size-full flex-col overflow-hidden", className);

  return (
    <StackNavigationProvider value={stackId}>
      <div className={containerClassName}>
        {/* Header */}
        {showHeader && (
          <div className="z-10 flex h-11 shrink-0 items-center gap-2 px-3">
            {canPop && (
              <Button variant="light" size="sm" isIconOnly onPress={handlePop} aria-label="Go back">
                <BackIcon />
              </Button>
            )}
            {currentTitle && (
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {currentTitle}
              </span>
            )}
            {headerEndContent && (
              <div className="ml-auto flex items-center">{headerEndContent}</div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="relative flex-1 overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            {stack.map((item: StackItem, index: number) => (
              <StackScreenRenderer
                key={item.key}
                item={item}
                index={index}
                isTop={index === stack.length - 1}
                canPop={canPop}
                onPop={handlePop}
                screens={componentMap}
                screenClassName={screenClassName}
                topIsReplace={stack[stack.length - 1]?.transitionType === "replace"}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </StackNavigationProvider>
  );
}

StackNavigator.displayName = "Stack.Navigator";
